import React, { useCallback, useMemo } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { ACTION_REJECTED, mapToErrorMessage } from '@components/Web3/utils'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { GetSigner } from 'privy/WalletReady'
import {
    SignerUndefinedError,
    useClaimRewardTransaction,
    useStakingRewardsQuery,
} from 'use-towns-client'
import { formatUnits } from 'viem'

export function useClaimMyRewards() {
    const { claimReward, ...rest } = useClaimRewardTransaction()
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()

    // Get current reward amount for analytics
    const { data: currentReward } = useStakingRewardsQuery({
        beneficiary: abstractAccountAddress,
        enabled: !!abstractAccountAddress,
    })

    const rewardAmount = useMemo(() => {
        return currentReward ? Number(formatUnits(currentReward.toBigInt(), 18)) : 0
    }, [currentReward])

    return {
        ...rest,
        claimReward: useCallback(
            async (args: {
                getSigner: GetSigner
                source: string
                action: 'claim' | 'claimAndStake' | 'redirectToStake'
                onSuccess?: () => void
            }) => {
                const { getSigner, source, onSuccess, action } = args

                if (!abstractAccountAddress) {
                    throw new Error('Abstract account address not available')
                }

                const signer = await getSigner()
                if (!signer) {
                    throw new SignerUndefinedError()
                }

                Analytics.getInstance().track('submit claim action', {
                    rewardAmount,
                    claimAction: action,
                })

                claimRewardToasts.submitting()

                return claimReward({
                    beneficiary: abstractAccountAddress,
                    recipient: abstractAccountAddress,
                    signer,
                    onSuccess: () => {
                        // Track claim success analytics
                        Analytics.getInstance().track('claim success', {
                            rewardAmount,
                            success: true,
                        })
                        claimRewardToasts.success('Reward claimed successfully!')
                        onSuccess?.()
                    },
                    onError: (error) => {
                        // Track claim failure analytics
                        Analytics.getInstance().track('claim success', {
                            rewardAmount,
                            success: false,
                        })
                        claimRewardToasts.error(error, source)
                    },
                })
            },
            [abstractAccountAddress, rewardAmount, claimReward],
        ),
    }
}

export const claimRewardToasts = {
    submitting: () => {
        popupToast(
            ({ toast }) => (
                <StandardToast.Pending toast={toast} message="Processing reward claim..." />
            ),
            {
                id: 'claim-rewards-tx',
                duration: Number.POSITIVE_INFINITY,
            },
        )
    },
    success: async (message: string) => {
        return popupToast(
            ({ toast }) => {
                return <StandardToast.Success toast={toast} message={message} />
            },
            {
                id: 'claim-rewards-tx',
            },
        )
    },
    error: (error: Error, source: string) => {
        const errorMessage = mapToErrorMessage({
            error,
            source,
        })

        if (errorMessage === ACTION_REJECTED) {
            return
        }

        popupToast(
            ({ toast }) => {
                return (
                    <StandardToast.Error
                        toast={toast}
                        message="There was an error claiming the reward."
                        subMessage={errorMessage}
                    />
                )
            },
            {
                id: 'claim-rewards-tx',
            },
        )
    },
}
